import { IReactionDisposer, action, computed, makeObservable, observable, reaction } from 'mobx';

import { IFormField, IFormFieldOptions, SetValueOptions } from '@/stores/interfaces/IFormField';
import { ErrorType, Validator, getEmptyError, orderValidators, validateNotEmpty } from '@/utils/validators';

export class FieldStore<T> implements IFormField<T> {
  protected readonly _validator?: Validator<T>;
  protected _defaultValue: T;
  protected readonly _defaultDisable: boolean;
  protected readonly _defaultRequired: boolean;

  private _requiredDisposers: IReactionDisposer[] = [];

  protected _touched = false;
  protected _value: T;
  protected _disable = false;
  protected _required = false;
  private _focused = false;
  /* Ошибка, которую можно прокинуть извне, чтобы подсветить поле... Например после неудачного запроса */
  private _syntheticError = getEmptyError();
  private _validationError = getEmptyError();

  constructor({ defaultValue, validator, disable = false, required = false }: IFormFieldOptions<T>) {
    this._validator = validator;
    this._value = defaultValue;
    this._defaultValue = defaultValue;
    this._disable = disable;
    this._defaultDisable = disable;
    this._required = typeof required === 'function' ? required() : required;
    this._defaultRequired = this._required;

    type PrivateFields =
      | '_touched'
      | '_value'
      | '_disable'
      | '_required'
      | '_focused'
      | '_syntheticError'
      | '_validationError'
      | '_defaultValue';

    makeObservable<FieldStore<T>, PrivateFields>(this, {
      _value: observable.ref,
      _touched: observable,
      _disable: observable,
      _required: observable,
      _focused: observable,
      _syntheticError: observable.ref,
      _validationError: observable.ref,
      validate: action.bound,
      error: computed,
      hasError: computed,
      focused: computed,
      disabled: computed,
      setDisabled: action.bound,
      required: computed,
      value: computed,
      touched: computed,
      hasChanged: computed,
      setValue: action.bound,
      reset: action.bound,
      resetValue: action.bound,
      touch: action.bound,
      unTouch: action.bound,
      blur: action.bound,
      focus: action.bound,
      setSyntheticError: action.bound,
      setDefaultValue: action.bound,
      _defaultValue: observable.ref,
    });

    this._requiredDisposers.push(
      reaction(
        () => this._value,
        () => {
          this.setSyntheticError(getEmptyError());
          this._validationError = getEmptyError();
        },
      ),
    );

    if (typeof required === 'function') {
      this._requiredDisposers.push(
        reaction(required, (isRequired) => {
          this.setRequired(isRequired);
        }),
      );
    }
  }
  get hasChanged(): boolean {
    return this._value !== this._defaultValue;
  }

  destroy(): void {
    this._requiredDisposers.forEach((d) => d());
  }

  get value(): T {
    return this._value;
  }

  setValue(value: T, options: SetValueOptions = {}): void {
    if (!options.noTouch) {
      this.touch();
    }

    this._value = value;
  }

  get disabled(): boolean {
    return this._disable;
  }

  setDisabled(value: boolean): void {
    this._disable = value;
  }

  get required(): boolean {
    return this._required;
  }

  setRequired(value: boolean): void {
    this._required = value;
  }

  setSyntheticError(error: ErrorType) {
    this._syntheticError = error;
  }

  reset(): void {
    this._touched = false;
    this._value = this._defaultValue;
    this._disable = this._defaultDisable;
    this._required = this._defaultRequired;
    this._syntheticError = getEmptyError();
    this._validationError = getEmptyError();
  }

  resetValue(): void {
    this.setValue(this._defaultValue);
  }

  setDefaultValue(value: T) {
    this._defaultValue = value;
  }

  get touched(): boolean {
    return this._touched;
  }

  get error(): ErrorType {
    if (this._syntheticError.value) {
      return this._syntheticError;
    }

    return this._validationError;
  }

  get hasError(): boolean {
    return Boolean(this.error.value);
  }

  get focused(): boolean {
    return this._focused;
  }

  validate(): void {
    this.setSyntheticError(getEmptyError());

    const validator = this._required
      ? this._validator
        ? orderValidators(validateNotEmpty(), this._validator)
        : validateNotEmpty()
      : this._validator;

    this._validationError = validator ? validator(this.value) : getEmptyError();
  }

  blur(): void {
    this._focused = false;
    this.touch();
    this.validate();
  }

  focus(): void {
    this._focused = true;
  }

  touch(): void {
    this._touched = true;
  }

  unTouch(): void {
    this._touched = false;
  }
}
